import Alert from '@v2/shared/components/Alert';
import Button from '@v2/shared/components/Button';
import Currency from '@v2/shared/components/Currency';
import TextButton from '@v2/shared/components/TextButton';
import { useSite } from '@v2/shared/lib/site';
import CancelWarningBox from './CancelWarningBox';

type Props = {
  autoRenewServiceName: string;
  /** What the next renewal would have cost. */
  originalPrice: number;
  /** What it would cost if this offer is taken. */
  discountedPrice: number;
  onAcceptOffer: (discountedPrice: number) => void;
  onDisable: () => void;
  onGoBack: () => void;
  loading?: boolean;
  acceptOfferLoading?: boolean;
  error?: Error;
};

/**
 * The discount a customer is offered on their way out.
 *
 * Both prices are required props rather than being dug out of the subscription here: they
 * only exist on a subscription that has an upcoming transaction carrying an offer, and
 * the modal has already established that before it renders this. A component that cannot
 * be handed the wrong thing does not need to check.
 */
export default function RetentionOfferForm({
  autoRenewServiceName,
  originalPrice,
  discountedPrice,
  onAcceptOffer,
  onDisable,
  onGoBack,
  loading = false,
  acceptOfferLoading = false,
  error,
}: Props) {
  const site = useSite();
  const savingsAmount = originalPrice - discountedPrice;
  const discountPercent = Math.round((savingsAmount / originalPrice) * 100);

  return (
    <div className="p-5 md:p-8">
      <h2 className="font-semibold text-2xl mb-4 tracking-tight text-center">Special Offer Just For You</h2>
      <p className="mb-6 font-medium text-center">
        Don&apos;t lose your {autoRenewServiceName} savings! We&apos;ll give you an additional{' '}
        <span className="font-semibold">{discountPercent}% off</span> your next renewal to keep you as a valued
        subscriber.
      </p>

      <div className="bg-green-50 border border-solid border-green-200 rounded-lg p-4 mb-6">
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm">Your Current Rate:</span>
            <span className="font-medium">
              <Currency value={originalPrice} currencyCode={site.currency} />
              /year
            </span>
          </div>
          <div className="flex justify-between items-center text-green-700">
            <span className="text-sm font-medium">Special Offer:</span>
            <span className="font-semibold">
              <Currency value={discountedPrice} currencyCode={site.currency} /> ({discountPercent}% off!)
            </span>
          </div>
          <hr className="border-0 border-t border-solid border-green-200" />
          <div className="flex justify-between items-center text-green-800">
            <span className="font-semibold">You Save:</span>
            <span className="font-bold text-lg">
              <Currency value={savingsAmount} currencyCode={site.currency} /> extra
            </span>
          </div>
        </div>
      </div>

      {error && <Alert color="danger">{error.message}</Alert>}

      <div className="flex justify-center mb-4">
        <Button
          onClick={() => onAcceptOffer(discountedPrice)}
          color="green-600"
          className="text-sm"
          isLoading={acceptOfferLoading}
          loadingText="Accepting Offer..."
        >
          Sounds Good!
        </Button>
      </div>

      <div className="flex justify-center mb-4">
        <TextButton onClick={onGoBack} className="text-sm">
          ← Go Back
        </TextButton>
      </div>

      <CancelWarningBox
        autoRenewServiceName={autoRenewServiceName}
        onDisable={onDisable}
        loading={loading}
      />
    </div>
  );
}
